import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import io from "socket.io-client";
import { useSelector } from "react-redux";
import { normalizeCanonicalStatus } from "../utils/deviceStatus";

export const SocketContext = createContext(null);

const SOCKET_CACHE_TTL_MS = 5 * 60 * 1000;
const IGNITION_ON_VALUES = new Set(["1", "true", "on", "yes", "acc_on", "ignition_on", "engine_on", "engine_start", "started"]);
const IGNITION_OFF_VALUES = new Set(["0", "false", "off", "no", "acc_off", "ignition_off", "engine_off", "engine_stop", "stopped"]);
const normalizeVehicleStatus = (value) => {
  return normalizeCanonicalStatus(value);
};

const sanitizeText = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
};

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toBoolean = (value, fallback = null) => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const lowered = value.trim().toLowerCase();
    if (IGNITION_ON_VALUES.has(lowered)) return true;
    if (IGNITION_OFF_VALUES.has(lowered)) return false;
  }
  return fallback;
};

const inferAccStatus = (payload = {}, fallback = null) => {
  const direct = toBoolean(
    payload.accStatus ??
      payload.acc ??
      payload.ignition ??
      payload.data?.accStatus ??
      payload.data?.acc ??
      payload.data?.ignition,
    null
  );
  if (direct !== null) return direct;

  const semanticCandidates = [
    payload.event,
    payload.alarmType,
    payload.type,
    payload.status,
    payload.vehicleStatus,
    payload.vehicle_status,
    payload.data?.status,
    payload.data?.vehicleStatus,
    payload.data?.vehicle_status,
  ];

  for (const candidate of semanticCandidates) {
    const parsed = toBoolean(candidate, null);
    if (parsed !== null) return parsed;
  }

  return fallback;
};

const resolveRealtimeVehicleStatus = (payload = {}, fallback = null) => {
  const resolved = normalizeVehicleStatus(
    payload?.liveData?.vehicleStatus ??
      payload?.vehicleStatus ??
      payload?.data?.vehicleStatus ??
      payload?.status ??
      payload?.data?.status
  );
  return resolved || normalizeVehicleStatus(fallback) || null;
};

const toTimestampMs = (value) => {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "number" && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (/^\d+$/.test(trimmed)) {
      const asNum = Number(trimmed);
      if (Number.isFinite(asNum)) return asNum < 1e12 ? asNum * 1000 : asNum;
    }
  }

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const getRealtimeOrderTime = (data = null) => {
  if (!data || typeof data !== "object") return 0;

  const sources = [
    data.serverReceivedAt,
    data.receivedAt,
    data.packetTime,
    data.timestamp,
    data.serverTime,
    data.deviceTimestamp,
    data.lastDataReceivedAt,
    data.lastUpdate,
    data.packetReceivedAt,
    data.lastSocketUpdate,
  ];

  for (const source of sources) {
    const parsed = toTimestampMs(source);
    if (!parsed) continue;

    // Guard against wildly wrong clocks causing UI drops.
    const now = Date.now();
    if (parsed < now - 365 * 24 * 60 * 60 * 1000) continue;
    if (parsed > now + 5 * 60 * 1000) continue;
    return parsed;
  }

  return 0;
};

const isValidLocation = (lat, lng) => {
  if (typeof lat !== "number" || typeof lng !== "number") return false;
  if (Number.isNaN(lat) || Number.isNaN(lng)) return false;
  if (lat === 0 && lng === 0) return false;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
  return true;
};

const prettyAlarmType = (value) => {
  if (!value) return "unknown";
  return String(value)
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
};

export const normalizeLocationPayload = (payload = {}) => {
  const lat = toFiniteNumber(payload.latitude ?? payload.lat ?? payload.location?.latitude ?? payload.location?.lat);
  const lng = toFiniteNumber(payload.longitude ?? payload.lng ?? payload.location?.longitude ?? payload.location?.lng);
  const packetTime = getRealtimeOrderTime(payload);
  const deviceTimeMs = toTimestampMs(payload.deviceTime ?? payload.rawDeviceTimestamp ?? payload.deviceTimestamp);
  const serverTimeMs = toTimestampMs(payload.serverTime ?? payload.serverReceivedAt ?? payload.receivedAt);
  const correctedTimeMs = toTimestampMs(payload.correctedTime ?? payload.timestamp ?? packetTime);
  const accStatus = inferAccStatus(payload, null);
  const speed = toFiniteNumber(payload.speed);
  const timestampIso = new Date(packetTime).toISOString();
  const vehicleStatus = resolveRealtimeVehicleStatus(payload, null);

  return {
    imei: payload.imei ? String(payload.imei) : null,
    deviceId: payload.deviceId || payload._id || payload.device?._id || null,
    latitude: lat,
    longitude: lng,
    lat,
    lng,
    speed,
    heading: toFiniteNumber(payload.heading ?? payload.course ?? payload.bearing),
    accStatus,
    vehicleStatus,
    movementState: sanitizeText(payload.movementState ?? payload.movement_state),
    direction: sanitizeText(payload.direction ?? payload.data?.direction),
    turnType: sanitizeText(payload.turnType ?? payload.turn_type ?? payload.data?.turnType ?? payload.data?.turn_type),
    timestamp: timestampIso,
    deviceTime: deviceTimeMs ? new Date(deviceTimeMs).toISOString() : null,
    serverTime: serverTimeMs ? new Date(serverTimeMs).toISOString() : null,
    correctedTime: correctedTimeMs ? new Date(correctedTimeMs).toISOString() : new Date(packetTime).toISOString(),
    isDrifted: Boolean(payload.isDrifted),
    driftMs: toFiniteNumber(payload.driftMs) ?? 0,
    driftSeconds: toFiniteNumber(payload.driftSeconds) ?? 0,
    packetTime,
    severity: payload.severity,
    raw: payload,
  };
};

export const normalizeAlarmPayload = (payload = {}) => {
  const base = payload?.alarm && typeof payload.alarm === "object" ? payload.alarm : payload;
  if (!base || (!base.alarmType && !base.type)) return null;

  const lat = toFiniteNumber(
    base.latitude ??
      base.lat ??
      base.location?.latitude ??
      base.location?.lat ??
      base.data?.location?.latitude ??
      base.data?.location?.lat
  );
  const lng = toFiniteNumber(
    base.longitude ??
      base.lng ??
      base.location?.longitude ??
      base.location?.lng ??
      base.data?.location?.longitude ??
      base.data?.location?.lng
  );

  const packetTime = getRealtimeOrderTime(base);
  const timestamp = new Date(packetTime).toISOString();
  const alarmType = String(base.alarmType || base.type || "unknown").trim();
  const accStatus = inferAccStatus(base, null);
  const speed = toFiniteNumber(base.speed ?? base.data?.speed) ?? 0;
  const vehicleStatus = resolveRealtimeVehicleStatus(base, null);

  return {
    alarmId:
      base._id ||
      base.alarmId ||
      base.id ||
      `${base.imei || "unknown"}-${alarmType}-${packetTime}`,
    alarmType,
    alarmLabel: prettyAlarmType(alarmType),
    severity: base.severity || "medium",
    status: base.status || "active",
    popupStatus: sanitizeText(base.popupStatus),
    forcePopup: Boolean(base.forcePopup),
    imei: base.imei ? String(base.imei) : null,
    deviceId: base.deviceId || base.device?._id || payload.deviceId || null,
    deviceName: base.deviceName || base.device?.name || null,
    latitude: lat,
    longitude: lng,
    lat,
    lng,
    speed,
    heading: toFiniteNumber(base.heading ?? base.data?.heading) ?? 0,
    sound: sanitizeText(payload?.sound ?? base.sound ?? base.data?.sound),
    accStatus,
    vehicleStatus,
    timestamp,
    packetTime,
    raw: base,
  };
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);

  const socketUpdatesRef = useRef({});
  const joinedDevicesRef = useRef(new Set());
  const subscribedImeisRef = useRef(new Set());
  const desiredImeisRef = useRef(new Set());
  const localListeners = useRef({});

  const user = useSelector(
    (state) =>
      state.auth?.user ||
      state.user?.data ||
      state.auth?.currentUser ||
      state.user?.currentUser ||
      state.auth?.userInfo
  );

  const emitLocal = useCallback((event, payload) => {
    (localListeners.current[event] || []).forEach((cb) => cb(payload));
  }, []);

  const emitDeviceSubscriptions = useCallback(
    (socketClient, eventName, imeis = []) => {
      if (!socketClient || !Array.isArray(imeis) || !imeis.length) return;
      const normalizedImeis = Array.from(
        new Set(
          imeis
            .map((imei) => String(imei || "").trim())
            .filter(Boolean)
        )
      );
      if (!normalizedImeis.length) return;

      if (normalizedImeis.length === 1) {
        socketClient.emit(eventName === "subscribe_devices" ? "subscribe_device" : "unsubscribe_device", normalizedImeis[0]);
        return;
      }

      socketClient.emit(eventName, normalizedImeis);
    },
    []
  );

  useEffect(() => {
    const cleanup = setInterval(() => {
      const now = Date.now();
      Object.keys(socketUpdatesRef.current).forEach((key) => {
        const entry = socketUpdatesRef.current[key];
        if (!entry?.lastSocketUpdate || now - entry.lastSocketUpdate > 5 * 60 * 1000) {
          delete socketUpdatesRef.current[key];
        }
      });
    }, 60 * 1000);

    return () => clearInterval(cleanup);
  }, []);

  const handleSocketUpdate = useCallback(
    (event, payload) => {
      if (!payload) return;

      const isLocationEvent =
        event === "location_update" ||
        event === "gps_update" ||
        event === "vehicle_update" ||
        event === "status_update" ||
        event === "device_update" ||
        event === "ignition_event";
      const isAlarmEvent = event === "alarm_event";

      if (isAlarmEvent) {
        const alarm = normalizeAlarmPayload(payload);
        if (!alarm) return;

        const key = alarm.deviceId || alarm.imei;
        if (key) {
          const prevEntry =
            socketUpdatesRef.current[key] ||
            (alarm.imei ? socketUpdatesRef.current[alarm.imei] : null) ||
            null;
          const prevPacketTime = Number(getRealtimeOrderTime(prevEntry) || 0);
          if (prevPacketTime > 0 && Number(alarm.packetTime || 0) > 0 && Number(alarm.packetTime) < prevPacketTime) {
            emitLocal("alarm_event", alarm);
            return;
          }

          let derivedAcc = alarm.accStatus;

          const packetTime = Number(alarm.packetTime || Date.now());
          const cacheEntry = {
            ...prevEntry,
            deviceId: alarm.deviceId || prevEntry?.deviceId || null,
            imei: alarm.imei || prevEntry?.imei || null,
            latitude: Number.isFinite(alarm.latitude) ? alarm.latitude : prevEntry?.latitude ?? null,
            longitude: Number.isFinite(alarm.longitude) ? alarm.longitude : prevEntry?.longitude ?? null,
            lat: Number.isFinite(alarm.latitude) ? alarm.latitude : prevEntry?.lat ?? prevEntry?.latitude ?? null,
            lng: Number.isFinite(alarm.longitude) ? alarm.longitude : prevEntry?.lng ?? prevEntry?.longitude ?? null,
            speed: Number.isFinite(alarm.speed) ? alarm.speed : prevEntry?.speed ?? 0,
            heading: Number.isFinite(alarm.heading) ? alarm.heading : prevEntry?.heading ?? 0,
            accStatus: derivedAcc ?? prevEntry?.accStatus ?? null,
            vehicleStatus: resolveRealtimeVehicleStatus(alarm.raw || alarm, prevEntry?.vehicleStatus ?? null),
            movementState:
              sanitizeText(alarm.raw?.movementState ?? alarm.raw?.movement_state) ||
              prevEntry?.movementState ||
              null,
            direction: sanitizeText(alarm.raw?.direction ?? prevEntry?.direction),
            turnType: sanitizeText(alarm.raw?.turnType ?? alarm.raw?.turn_type ?? prevEntry?.turnType),
            timestamp: alarm.timestamp || new Date(packetTime).toISOString(),
            packetTime,
            packetAge: Date.now() - packetTime,
            isFresh: true,
            lastSocketUpdate: Date.now(),
            packetReceivedAt: Date.now(),
            raw: alarm.raw || payload,
          };

          if (cacheEntry.deviceId) socketUpdatesRef.current[cacheEntry.deviceId] = cacheEntry;
          if (cacheEntry.imei) socketUpdatesRef.current[cacheEntry.imei] = cacheEntry;
          emitLocal("location_update", cacheEntry);
          emitLocal("status_update", cacheEntry);
        }

        emitLocal("alarm_event", alarm);
        return;
      }

      if (!isLocationEvent) {
        emitLocal(event, payload);
        return;
      }

      const normalized = normalizeLocationPayload(payload);
      if (!normalized.deviceId && !normalized.imei) return;

      const key = normalized.deviceId || normalized.imei;
      const prevEntry =
        (normalized.deviceId ? socketUpdatesRef.current[normalized.deviceId] : null) ||
        (normalized.imei ? socketUpdatesRef.current[normalized.imei] : null) ||
        socketUpdatesRef.current[key] ||
        null;
      const resolvedDeviceId = normalized.deviceId || prevEntry?.deviceId || null;
      const resolvedImei = normalized.imei || prevEntry?.imei || null;
      const cacheKeys = [key, resolvedDeviceId, resolvedImei].filter(Boolean);
      const hasIncomingLocation = isValidLocation(normalized.latitude, normalized.longitude);
      const hasExplicitCoordinates =
        payload?.latitude !== undefined ||
        payload?.lat !== undefined ||
        payload?.longitude !== undefined ||
        payload?.lng !== undefined;
      const prevPacketTime = Number(getRealtimeOrderTime(prevEntry) || 0);
      const incomingPacketTime = Number(normalized.packetTime || 0);

      if (incomingPacketTime > 0 && prevPacketTime > 0 && incomingPacketTime < prevPacketTime) {
        return;
      }

      if (hasExplicitCoordinates && !hasIncomingLocation) {
        return;
      }

      let locationInherited = false;
      if (!hasIncomingLocation) {
        // Allow status-only packets (ignition/vehicle status/speed) to update UI
        // even when tracker does not send GPS coordinates in that frame.
        if (isValidLocation(prevEntry?.latitude, prevEntry?.longitude)) {
          normalized.latitude = prevEntry.latitude;
          normalized.longitude = prevEntry.longitude;
          normalized.lat = prevEntry.latitude;
          normalized.lng = prevEntry.longitude;
          locationInherited = true;
        } else {
          normalized.latitude = null;
          normalized.longitude = null;
          normalized.lat = null;
          normalized.lng = null;
        }
      }

      const age = Date.now() - normalized.packetTime;
      const normalizedSpeed =
        Number.isFinite(normalized.speed) && normalized.speed >= 0
          ? normalized.speed
          : prevEntry?.speed ?? 0;
      normalized.vehicleStatus = resolveRealtimeVehicleStatus(payload, prevEntry?.vehicleStatus ?? null);

      const cacheEntry = {
        ...prevEntry,
        ...normalized,
        deviceId: resolvedDeviceId,
        imei: resolvedImei,
        speed: normalizedSpeed,
        heading:
          normalized.heading !== null && normalized.heading !== undefined
            ? normalized.heading
            : prevEntry?.heading ?? null,
        vehicleStatus: normalized.vehicleStatus || prevEntry?.vehicleStatus || null,
        movementState: normalized.movementState || prevEntry?.movementState || null,
        accStatus: normalized.accStatus ?? prevEntry?.accStatus ?? null,
        direction:
          normalized.direction ||
          prevEntry?.direction ||
          null,
        turnType:
          normalized.turnType ||
          prevEntry?.turnType ||
          null,
        hasLiveLocation: hasIncomingLocation,
        locationInherited,
        locationPacketTime:
          hasIncomingLocation
            ? normalized.packetTime
            : Number(prevEntry?.locationPacketTime || prevEntry?.packetTime || 0) || null,
        packetAge: age,
        isFresh: age < 15 * 1000,
        lastSocketUpdate: Date.now(),
        packetReceivedAt: Date.now(),
        lastUpdate: normalized.timestamp,
      };

      cacheKeys.forEach((cacheKey) => {
        socketUpdatesRef.current[cacheKey] = cacheEntry;
      });
      emitLocal("status_update", cacheEntry);
      emitLocal("gps_packet", cacheEntry);
      if (event !== "location_update") {
        emitLocal("location_update", cacheEntry);
      }
      emitLocal(event, cacheEntry);
    },
    [emitLocal]
  );

  useEffect(() => {
    if (!user?._id && !user?.id) return;

    const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || "https://app.krush.in";
    const SOCKET_PATH = process.env.REACT_APP_SOCKET_PATH || "/socket.io";
    const token = localStorage.getItem("token");

    const socketClient = io(SOCKET_URL, {
      auth: { token },
      path: SOCKET_PATH,
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 20,
      reconnectionDelay: 1000,
      autoConnect: true,
      timeout: 10000,
    });

    socketClient.on("connect", () => {
      setConnected(true);
      emitLocal("socket_connection", { status: "connected", socketId: socketClient.id });

      const uid = user._id || user.id;
      socketClient.emit("authenticate", uid);

      const userRole = user.role;
      if (userRole === "admin" || userRole === "Admin") {
        socketClient.emit("join", `admin_${uid}`);
      } else {
        socketClient.emit("join", `user_${uid}`);
      }

      emitDeviceSubscriptions(socketClient, "subscribe_devices", Array.from(desiredImeisRef.current));
    });

    socketClient.on("reconnect", () => {
      emitLocal("socket_connection", { status: "reconnected", socketId: socketClient.id });
      emitDeviceSubscriptions(socketClient, "subscribe_devices", Array.from(desiredImeisRef.current));
    });

    socketClient.on("disconnect", (reason) => {
      setConnected(false);
      console.debug("[SOCKET] onclose", { reason });
      emitLocal("socket_connection", { status: "disconnected", reason });
      joinedDevicesRef.current.clear();
      subscribedImeisRef.current.clear();
    });

    socketClient.on("connect_error", (error) => {
      setConnected(false);
      console.debug("[SOCKET] onerror", { message: error?.message || "Connection error" });
      emitLocal("socket_connection", {
        status: "error",
        reason: error?.message || "Connection error",
      });
    });

    socketClient.on("error", (error) => {
      console.debug("[SOCKET] onerror", { message: error?.message || String(error) });
    });

    socketClient.on("subscriptions_ready", (data) => {
      if (Array.isArray(data?.devices)) {
        data.devices.forEach((imei) => subscribedImeisRef.current.add(imei));
      }
    });

    socketClient.on("location", (data) => handleSocketUpdate("location_update", data));
    socketClient.on("location_update", (data) => handleSocketUpdate("location_update", data));
    socketClient.on("gps_update", (data) => handleSocketUpdate("gps_update", data));
    socketClient.on("vehicle_update", (data) => handleSocketUpdate("vehicle_update", data));
    socketClient.on("status_update", (data) => handleSocketUpdate("status_update", data));
    socketClient.on("device_update", (data) => handleSocketUpdate("device_update", data));
    socketClient.on("ignition_event", (data) => handleSocketUpdate("ignition_event", data));

    socketClient.on("alarm_event", (data) => handleSocketUpdate("alarm_event", data));
    socketClient.on("alarm_status_update", (data) => emitLocal("alarm_status_update", data));
    socketClient.on("command:success", (data) => emitLocal("command:success", data));
    socketClient.on("command:failed", (data) => emitLocal("command:failed", data));
    socketClient.on("command:queued", (data) => emitLocal("command:queued", data));
    socketClient.on("command:status", (data) => emitLocal("command:status", data));
    socketClient.on("command:response", (data) => emitLocal("command:response", data));

    socketClient.on("calendar:popup", (payload) => {
      emitLocal("calendar:popup", payload);
      window.dispatchEvent(new CustomEvent("calendar-popup", { detail: payload }));
    });

    setSocket(socketClient);
    const subscribedImeis = subscribedImeisRef.current;
    const desiredImeis = desiredImeisRef.current;

    return () => {
      socketClient.removeAllListeners();
      socketClient.close();
      setSocket(null);
      setConnected(false);
      socketUpdatesRef.current = {};
      joinedDevicesRef.current.clear();
      subscribedImeis.clear();
      desiredImeis.clear();
    };
  }, [user?._id, user?.id, user?.role, handleSocketUpdate, emitLocal, emitDeviceSubscriptions]);

  const getDeviceSocketData = useCallback((deviceIdOrImei) => {
    if (!deviceIdOrImei) return {};
    const data = socketUpdatesRef.current[deviceIdOrImei];
    if (!data) return {};
    if (!data.lastSocketUpdate || Date.now() - data.lastSocketUpdate > SOCKET_CACHE_TTL_MS) {
      return {};
    }
    return data;
  }, []);

  const clearSocketCache = useCallback((deviceIdOrImei = null) => {
    if (deviceIdOrImei) {
      delete socketUpdatesRef.current[deviceIdOrImei];
      return;
    }

    socketUpdatesRef.current = {};
  }, []);

  const emit = useCallback(
    (event, data) => {
      if (socket && connected) socket.emit(event, data);
    },
    [socket, connected]
  );

  const on = useCallback((event, cb) => {
    if (!cb) return () => {};
    localListeners.current[event] = localListeners.current[event] || [];
    localListeners.current[event].push(cb);

    return () => {
      localListeners.current[event] = (localListeners.current[event] || []).filter(
        (listener) => listener !== cb
      );
    };
  }, []);

  const off = useCallback((event, cb) => {
    localListeners.current[event] = (localListeners.current[event] || []).filter(
      (listener) => listener !== cb
    );
  }, []);

  const subscribeToDevice = useCallback(
    (imei) => {
      if (!imei) return;
      const normalizedImei = String(imei);
      desiredImeisRef.current.add(normalizedImei);

      if (!socket || !connected) return;
      if (subscribedImeisRef.current.has(normalizedImei)) return;

      emitDeviceSubscriptions(socket, "subscribe_devices", [normalizedImei]);
      subscribedImeisRef.current.add(normalizedImei);
    },
    [socket, connected, emitDeviceSubscriptions]
  );

  const subscribeToDevices = useCallback(
    (imeis = []) => {
      const normalizedImeis = Array.from(
        new Set(
          imeis
            .map((imei) => String(imei || "").trim())
            .filter(Boolean)
        )
      );
      if (!normalizedImeis.length) return;

      normalizedImeis.forEach((imei) => desiredImeisRef.current.add(imei));

      if (!socket || !connected) return;

      const imeisToSubscribe = normalizedImeis.filter((imei) => !subscribedImeisRef.current.has(imei));
      if (!imeisToSubscribe.length) return;

      emitDeviceSubscriptions(socket, "subscribe_devices", imeisToSubscribe);
      imeisToSubscribe.forEach((imei) => subscribedImeisRef.current.add(imei));
    },
    [socket, connected, emitDeviceSubscriptions]
  );

  const unsubscribeFromDevice = useCallback(
    (imei) => {
      if (!imei) return;
      const normalizedImei = String(imei);
      desiredImeisRef.current.delete(normalizedImei);

      if (!socket || !connected) return;
      if (!subscribedImeisRef.current.has(normalizedImei)) return;

      emitDeviceSubscriptions(socket, "unsubscribe_devices", [normalizedImei]);
      subscribedImeisRef.current.delete(normalizedImei);
    },
    [socket, connected, emitDeviceSubscriptions]
  );

  const unsubscribeFromDevices = useCallback(
    (imeis = []) => {
      const normalizedImeis = Array.from(
        new Set(
          imeis
            .map((imei) => String(imei || "").trim())
            .filter(Boolean)
        )
      );
      if (!normalizedImeis.length) return;

      normalizedImeis.forEach((imei) => desiredImeisRef.current.delete(imei));

      if (!socket || !connected) return;

      const imeisToRemove = normalizedImeis.filter((imei) => subscribedImeisRef.current.has(imei));
      if (!imeisToRemove.length) return;

      emitDeviceSubscriptions(socket, "unsubscribe_devices", imeisToRemove);
      imeisToRemove.forEach((imei) => subscribedImeisRef.current.delete(imei));
    },
    [socket, connected, emitDeviceSubscriptions]
  );

  const joinDevice = useCallback(
    (deviceId) => {
      if (!socket || !connected || !deviceId) return;
      if (joinedDevicesRef.current.has(deviceId)) return;

      socket.emit("join_device", deviceId);
      joinedDevicesRef.current.add(deviceId);
    },
    [socket, connected]
  );

  const leaveDevice = useCallback(
    (deviceId) => {
      if (!socket || !connected || !deviceId) return;
      if (!joinedDevicesRef.current.has(deviceId)) return;

      socket.emit("leave_device", deviceId);
      joinedDevicesRef.current.delete(deviceId);
    },
    [socket, connected]
  );

  return (
    <SocketContext.Provider
      value={{
        socket,
        connected,
        userId: user?._id || user?.id,
        socketUpdatesRef,
        emit,
        on,
        off,
        joinDevice,
        leaveDevice,
        subscribeToDevice,
        subscribeToDevices,
        unsubscribeFromDevice,
        unsubscribeFromDevices,
        getDeviceSocketData,
        clearSocketCache,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within SocketProvider");
  }
  return context;
};
